import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { createPortal } from 'react-dom';

import styles from './DismissVisitModal.module.scss';
import { DISMISS_REASONS as REASONS } from './visitsDue';

const todayStr = () => new Date().toISOString().slice(0, 10);

const addDays = (days) => {
  const date = new Date();
  date.setDate(date.getDate() + days);
  return date.toISOString().slice(0, 10);
};

const DismissVisitModal = ({ patientName, onClose, onDefer, onDismiss, isSubmitting = false }) => {
  const [reason, setReason] = useState(null);
  const [reevalOn, setReevalOn] = useState('');
  const [serviceDate, setServiceDate] = useState('');
  const [note, setNote] = useState('');
  const today = todayStr();

  const selected = REASONS.find((r) => r.key === reason);
  const showsDate = selected && (selected.defaultReevalDays != null || selected.dateOptional);
  const dateRequired = showsDate && !selected.dateOptional;

  const pick = (r) => {
    setReason(r.key);
    setReevalOn(r.defaultReevalDays && !r.dateOptional ? addDays(r.defaultReevalDays) : '');
    setServiceDate('');
  };

  const valid =
    !!reason &&
    (!selected?.noteRequired || note.trim()) &&
    (!dateRequired || (reevalOn && reevalOn > today)) &&
    (!reevalOn || reevalOn > today) &&
    (!selected?.asksServiceDate || (serviceDate && serviceDate <= today));

  const modalContent = (
    <div
      className={styles.modalOverlay}
      role="presentation"
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        className={styles.modalCard}
        role="dialog"
        aria-modal="true"
        aria-labelledby="dismiss-visit-title"
      >
        <div className={styles.modalHeader}>
          <div id="dismiss-visit-title" className={styles.modalTitle}>
            Dismiss {patientName ? `${patientName.split(' ')[0]}'s visit` : 'visit'}
          </div>
          <div className={styles.modalSubtitle}>
            Pick a look-again date to defer it, or leave it blank to close it out.
          </div>
        </div>

        <div className={styles.modalBody}>
          <div className={styles.sectionLabel}>Reason</div>
          <fieldset className={styles.reasonList}>
            {REASONS.map((r) => (
              <label
                key={r.key}
                htmlFor={`dismiss-visit-reason-${r.key}`}
                className={`${styles.reasonRow} ${reason === r.key ? styles.reasonRowActive : ''}`}
              >
                <input
                  id={`dismiss-visit-reason-${r.key}`}
                  type="radio"
                  name="dismiss-visit-reason"
                  checked={reason === r.key}
                  onChange={() => pick(r)}
                />
                {r.label}
              </label>
            ))}
          </fieldset>

          {showsDate && (
            <label className={styles.dateLabel} htmlFor="dismiss-visit-reeval-on">
              Look again on{selected.dateOptional ? ' (optional; leave empty to dismiss)' : ''}
              <input
                id="dismiss-visit-reeval-on"
                type="date"
                className={styles.dateInput}
                value={reevalOn}
                min={today}
                onChange={(event) => setReevalOn(event.target.value)}
              />
            </label>
          )}

          {selected?.asksServiceDate && (
            <label className={styles.dateLabel} htmlFor="dismiss-visit-service-date">
              Service date
              <input
                id="dismiss-visit-service-date"
                type="date"
                className={styles.dateInput}
                value={serviceDate}
                max={today}
                onChange={(event) => setServiceDate(event.target.value)}
              />
            </label>
          )}

          {reason && (
            <label className={styles.dateLabel} htmlFor="dismiss-visit-note">
              Note{selected?.noteRequired ? '' : ' (optional)'}
              <textarea
                id="dismiss-visit-note"
                className={styles.noteInput}
                rows={3}
                value={note}
                onChange={(event) => setNote(event.target.value)}
              />
            </label>
          )}
        </div>

        <div className={styles.modalFooter}>
          <button type="button" onClick={onClose} className={styles.modalSecondaryAction}>
            Cancel
          </button>
          <button
            type="button"
            className={styles.modalPrimaryAction}
            disabled={!valid || isSubmitting}
            onClick={async () => {
              const noteText =
                [serviceDate ? `service date ${serviceDate}` : null, note.trim()]
                  .filter(Boolean)
                  .join(' — ') || null;
              const shouldClose = reevalOn
                ? await onDefer?.(reevalOn, selected?.label, noteText)
                : await onDismiss?.(selected?.label, noteText);
              if (shouldClose !== false) onClose();
            }}
          >
            {isSubmitting ? 'Working…' : reevalOn ? 'Defer' : 'Dismiss'}
          </button>
        </div>
      </div>
    </div>
  );

  if (typeof document === 'undefined') return modalContent;

  return createPortal(modalContent, document.body);
};

DismissVisitModal.propTypes = {
  patientName: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onDefer: PropTypes.func.isRequired,
  onDismiss: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool,
};

export default DismissVisitModal;
